import { hasConsent } from '@/lib/consent';
import { getSessionId } from './useSessionTracking';

import type { IntentActionType } from './useSessionIntelligence';

// Session intelligence update function (lazy loaded to avoid circular deps)
let setIntentSignalFn: ((actionType?: IntentActionType) => void) | null = null;
export const registerIntentSignalHandler = (fn: (actionType?: IntentActionType) => void) => {
  setIntentSignalFn = fn;
};

export type IntentAction = 
  | 'click_call' 
  | 'click_webinar' 
  | 'click_waitlist' 
  | 'click_whatsapp'
  | 'click_contact'
  | 'click_sleep_scan'
  | 'click_podcast'
  | 'click_download'
  | 'cta_click';

interface CTAClickPayload {
  cta_name: string;
  destination?: string;
  page: string;
}

/**
 * Tracks user intent signals (CTA interactions)
 * @param actionType - The type of intent action (e.g., click_call, click_webinar)
 * @param page - Optional page path (defaults to current location)
 */
export const trackIntentSignal = async (
  actionType: IntentAction,
  page?: string
): Promise<void> => {
  if (!hasConsent('statistics')) return;
  const sessionId = getSessionId();
  const currentPage = page || window.location.pathname;

  const payload = {
    event_type: 'intent_signal',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      action_type: actionType,
      page: currentPage,
    },
  };

  void payload;

  // Keep CTA intelligence local only. Browser-side calls to the pulse endpoint
  // are intentionally disabled because ad blockers flag them and Lighthouse
  // reports ERR_BLOCKED_BY_CLIENT console errors even when JS catches failures.
  if (setIntentSignalFn) {
    setIntentSignalFn(actionType as IntentActionType);
  }
};

/**
 * Tracks a CTA click event with detailed payload
 * @param ctaName - Name/identifier of the CTA
 * @param destination - Optional destination URL/path
 */
export const trackCTAClickEvent = async (
  ctaName: string,
  destination?: string
): Promise<void> => {
  if (!hasConsent('statistics')) return;
  const sessionId = getSessionId();
  const currentPage = window.location.pathname;

  const payload = {
    event_type: 'cta_click',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      cta_name: ctaName,
      destination: destination || null,
      page: currentPage,
    } as CTAClickPayload,
  };

  void payload;
};

/**
 * Creates a click handler that tracks intent and optionally executes a callback
 */
export const createIntentHandler = (
  actionType: IntentAction,
  callback?: () => void
) => {
  return () => {
    trackIntentSignal(actionType);
    if (callback) {
      callback();
    }
  };
};

/**
 * Creates a CTA click handler
 */
export const createCTAHandler = (
  ctaName: string,
  destination?: string,
  callback?: () => void
) => {
  return () => {
    trackCTAClickEvent(ctaName, destination);
    if (callback) {
      callback();
    }
  };
};

/**
 * Tracks intent signal using sendBeacon (for external links/navigation)
 */
export const trackIntentSignalBeacon = (
  actionType: IntentAction,
  page?: string
): void => {
  if (!hasConsent('statistics')) return;
  const sessionId = getSessionId();
  const currentPage = page || window.location.pathname;
  const payload = {
    event_type: 'intent_signal',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      action_type: actionType,
      page: currentPage,
    },
  };

  void payload;
};

/**
 * Tracks CTA click using sendBeacon (for external links)
 */
export const trackCTAClickBeacon = (
  ctaName: string,
  destination?: string
): void => {
  if (!hasConsent('statistics')) return;
  const sessionId = getSessionId();
  const payload = {
    event_type: 'cta_click',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      cta_name: ctaName,
      destination: destination || null,
      page: window.location.pathname,
    },
  };

  void payload;
};
